import { useMemo } from 'react';

import { fuzzyMatch, useSearchAndCollapse } from '@hooks/use-search-and-collapse';

import { WorkspaceWithJourneyForms } from 'api/portals/requests';

export const useWorkspaceSearch = (workspaces: WorkspaceWithJourneyForms[]) => {
  const {
    searchStr,
    setSearchStr,
    filteredData,
    collapsedState,
    expandCollapseText,
    handleExpandCollapse,
    toggleCollapse,
  } = useSearchAndCollapse({
    data: workspaces,
    getGroupKey: (workspace) => workspace.shortId,
    searchFilter: (workspace, searchStr) => {
      if (fuzzyMatch(workspace.title, searchStr)) {
        return true;
      }

      if (!workspace.forms || workspace.forms.length === 0) {
        return false;
      }

      return workspace.forms.some((form) => {
        return (
          fuzzyMatch(form.name, searchStr) ||
          fuzzyMatch(form.portalDescription, searchStr) ||
          fuzzyMatch(form.journeyName, searchStr)
        );
      });
    },
    localStorageKey: 'workspace-search',
  });

  const visibleWorkspaces = useMemo(() => {
    if (!searchStr.trim()) {
      return filteredData;
    }

    return filteredData.map((workspace) => {
      if (fuzzyMatch(workspace.title, searchStr)) {
        return workspace;
      }

      const filteredForms =
        workspace.forms?.filter((form) => {
          return (
            fuzzyMatch(form.name, searchStr) ||
            fuzzyMatch(form.portalDescription, searchStr) ||
            fuzzyMatch(form.journeyName, searchStr)
          );
        }) ?? [];

      return {
        ...workspace,
        forms: filteredForms,
      };
    });
  }, [filteredData, searchStr]);

  return {
    searchStr,
    setSearchStr,
    visibleWorkspaces,
    collapsedState,
    expandCollapseText,
    handleExpandCollapse,
    toggleCollapse,
  };
};
